<script setup lang="ts">
import { faIconStyle } from '~/utils/faIconStyle'
import type { Workshop } from '~/composables/useWorkshops'
import { useWorkshops } from '~/composables/useWorkshops'

const props = defineProps<{
    workshop: Workshop
}>()

const { isFull } = useWorkshops()

const showForm = ref(false)

const formatDate = (iso: string | null) => {
    if (!iso) return null
    return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
}

const spotsLeft = computed(() => {
    if (!props.workshop.maxParticipants) return null
    return props.workshop.maxParticipants - props.workshop.currentParticipants
})
</script>

<template>
    <article class="group relative bg-oxo-card-bg rounded-2xl border border-oxo-border overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-2 hover:shadow-xl dark:hover:shadow-[0_0_30px_rgba(255,255,255,0.05)] min-h-[440px]">

        <!-- Background Image & Overlay -->
        <div class="absolute inset-0 z-0">
            <NuxtImg loading="lazy"
                v-if="workshop.imageUrl"
                :src="workshop.imageUrl"
                :alt="workshop.imageAlt ?? workshop.title"
                class="w-full h-full object-cover opacity-40 group-hover:opacity-60 transition-opacity duration-500 scale-100 group-hover:scale-110 transform"
                sizes="100vw md:50vw lg:33vw"
            />
            <div v-else class="absolute inset-0 bg-oxo-red/30"></div>
            <div class="absolute inset-0 bg-card-overlay-gradient z-10 transition-opacity duration-300 group-hover:opacity-90"></div>
        </div>

        <!-- Badge top-right -->
        <div class="absolute top-4 right-4 z-20 pointer-events-none">
            <UiBadge v-if="isFull(workshop)" variant="primary">Complet</UiBadge>
            <UiBadge v-else-if="spotsLeft !== null" variant="success">{{ spotsLeft }} place{{ spotsLeft !== 1 ? 's' : '' }} restante{{ spotsLeft !== 1 ? 's' : '' }}</UiBadge>
        </div>

        <!-- Content -->
        <div class="relative z-10 p-6 flex flex-col flex-1 gap-4">

            <!-- Icon -->
            <div class="mb-2 inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/10 pointer-events-none">
                <i :style="faIconStyle('fas fa-' + (workshop.iconName ?? 'fa-gamepad').replace('fa-', ''))" :class="'fas fa-' + (workshop.iconName ?? 'fa-gamepad').replace('fa-', '') + ' text-2xl text-white'" aria-hidden="true"></i>
            </div>

            <!-- Title & meta -->
            <div>
                <h3 class="font-display font-bold text-xl text-white drop-shadow-lg mb-1">{{ workshop.title }}</h3>
                <p class="text-white/70 text-sm">{{ workshop.durationDays }} jour{{ workshop.durationDays > 1 ? 's' : '' }} · {{ workshop.ageMin }} ans et +</p>
            </div>

            <!-- Description -->
            <p class="text-gray-100 text-sm leading-relaxed grow drop-shadow-md">{{ workshop.description }}</p>

            <!-- Dates -->
            <div v-if="workshop.startDate" class="flex items-center gap-2 text-sm text-white/80">
                <i class="fas fa-calendar text-oxo-yellow" aria-hidden="true"></i>
                <span>Du {{ formatDate(workshop.startDate) }} au {{ formatDate(workshop.endDate) }}</span>
            </div>

            <!-- Price -->
            <div class="flex items-center gap-2 text-sm font-bold text-white">
                <i class="fas fa-tag text-oxo-yellow" aria-hidden="true"></i>
                <span>{{ workshop.price.toFixed(2) }} €</span>
            </div>

            <!-- Itch.io link -->
            <a
                v-if="workshop.itchIoUrl"
                :href="workshop.itchIoUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-2 text-sm text-oxo-yellow hover:text-white transition-colors"
            >
                <i class="fas fa-gamepad" aria-hidden="true"></i>
                <span>Voir les jeux créés lors des ateliers</span>
            </a>

            <!-- CTA -->
            <div class="mt-auto pt-2">
                <!-- WeezEvent mode -->
                <a
                    v-if="workshop.weezEventUrl"
                    :href="workshop.weezEventUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    :class="[
                        'btn-primary w-full sm:w-full! text-center block',
                        isFull(workshop) ? 'opacity-50 pointer-events-none' : ''
                    ]"
                >
                    En savoir plus
                </a>

                <!-- Stripe mode -->
                <template v-else>
                    <button
                        v-if="!showForm"
                        :disabled="isFull(workshop)"
                        :class="['btn-primary w-full sm:w-full!', isFull(workshop) ? 'opacity-50 cursor-not-allowed' : '']"
                        @click="showForm = true"
                    >
                        {{ isFull(workshop) ? 'Complet' : 'En savoir plus' }}
                    </button>

                    <WorkshopsWorkshopRegistrationForm
                        v-else
                        :workshop-id="workshop.id"
                        :workshop-title="workshop.title"
                        :workshop-price="workshop.price"
                        @cancel="showForm = false"
                    />
                </template>
            </div>
        </div>
    </article>
</template>
